/**
 * Auto-Forward source mailboxes.
 *
 * A rule used to carry a single `sourceAccountId` / `sourceEmail`. It can now
 * list several mailboxes in `sources`. These helpers hydrate the list from
 * either shape so the settings UI and saved cards stay backward compatible.
 */

const asSource = (accountId, email) => ({
  accountId: typeof accountId === "string" ? accountId.trim() : "",
  email: typeof email === "string" ? email.trim().toLowerCase() : "",
});

const isUsable = (source) => !!(source.accountId || source.email);

/**
 * Prefer the `sources` array. Fall back to the legacy single-mailbox fields
 * so rules saved before multi-select still open and display correctly.
 */
export const hydrateAutoForwardSources = (rule = {}) => {
  if (Array.isArray(rule.sources) && rule.sources.length > 0) {
    const seenIds = new Set();
    const seenEmails = new Set();
    const out = [];
    rule.sources.forEach((item) => {
      const source = asSource(item && item.accountId, item && item.email);
      if (!isUsable(source)) return;
      if (source.accountId && seenIds.has(source.accountId)) return;
      if (source.email && seenEmails.has(source.email)) return;
      if (source.accountId) seenIds.add(source.accountId);
      if (source.email) seenEmails.add(source.email);
      out.push(source);
    });
    if (out.length > 0) return out;
  }

  const legacy = asSource(rule.sourceAccountId, rule.sourceEmail);
  return isUsable(legacy) ? [legacy] : [];
};

export const sourceEmailsOf = (sources = []) =>
  sources.map((s) => (s && s.email) || "").filter(Boolean);

export const toggleAutoForwardSource = (sources, account) => {
  const current = Array.isArray(sources) ? sources : [];
  const id = account && account.id;
  if (!id) return current;
  const exists = current.some((s) => s.accountId === id);
  if (exists) return current.filter((s) => s.accountId !== id);
  return [...current, asSource(id, account.email)];
};

/** First source kept on the rule so older readers still see a mailbox. */
export const legacySourceFields = (sources = []) => {
  const first = sources[0];
  return {
    sourceAccountId: (first && first.accountId) || "",
    sourceEmail: (first && first.email) || "",
  };
};
